import { ref, watch } from 'vue';
import { debounce } from '@chatwoot/utils';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

/**
 * Espelha CONVERSATION_RESULTS_PER_PAGE do backend. Página cheia é o único
 * sinal de "tem mais" que o endpoint de busca dá — ele não devolve total.
 */
export const SEARCH_RESULTS_PER_PAGE = 25;

const DEBOUNCE_MS = 300;

/**
 * Estado inteiro da busca da lista de conversas: a consulta, os resultados,
 * a paginação, o erro e o aviso de que o backend cortou o resultado.
 *
 * Saiu de dentro do ChatList (~1500 linhas) porque a busca é a única parte
 * dele com ciclo de vida próprio — e porque testar isso montando o ChatList
 * inteiro é o motivo de nunca ter tido teste.
 *
 * @param {Object} params
 * @param {import('vue').Ref<string>} params.assigneeType - aba ativa
 * @param {import('vue').Ref<string>} params.status - filtro de status ativo
 */
export function useConversationSearch({ assigneeType, status }) {
  const query = ref('');
  const results = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const page = ref(1);
  const hasMore = ref(false);
  // O backend limita o casamento por conteúdo de mensagem; quando bate no
  // teto ele avisa aqui e a tela pede pra refinar em vez de fingir que acabou.
  const truncated = ref(false);

  const reset = () => {
    results.value = [];
    error.value = null;
    page.value = 1;
    hasMore.value = false;
    truncated.value = false;
    isLoading.value = false;
  };

  /**
   * @param {string} q
   * @param {number} nextPage - 1 substitui a lista; maior concatena.
   */
  const run = async (q, nextPage = 1) => {
    isLoading.value = true;
    error.value = null;
    try {
      const { data } = await ConversationApi.search({
        q,
        assigneeType: assigneeType.value,
        status: status.value,
        page: nextPage,
      });
      const payload = data?.payload || [];
      results.value = nextPage === 1 ? payload : [...results.value, ...payload];
      page.value = nextPage;
      hasMore.value = payload.length >= SEARCH_RESULTS_PER_PAGE;
      truncated.value = Boolean(data?.meta?.truncated);
    } catch (apiError) {
      // Busca que falhava ficava idêntica a "não achei nada": o catch zerava
      // a lista e a tela caía no vazio de sempre. Erro é erro.
      if (nextPage === 1) results.value = [];
      hasMore.value = false;
      error.value = parseAPIErrorResponse(apiError);
    } finally {
      isLoading.value = false;
    }
  };

  const debouncedRun = debounce(q => {
    if (!q) {
      reset();
      return;
    }
    run(q, 1);
  }, DEBOUNCE_MS);

  /** Próxima página. No-op quando não há mais nada ou já tem busca em voo. */
  const loadNextPage = () => {
    const trimmed = query.value.trim();
    if (!trimmed || isLoading.value || !hasMore.value) return;
    run(trimmed, page.value + 1);
  };

  const retry = () => {
    const trimmed = query.value.trim();
    if (!trimmed) return;
    debouncedRun.cancel?.();
    run(trimmed, 1);
  };

  /** Recomeça da primeira página — usado quando aba/status mudam. */
  const rerun = () => {
    const trimmed = query.value.trim();
    if (!trimmed) return;
    page.value = 1;
    hasMore.value = false;
    isLoading.value = true;
    debouncedRun.cancel?.();
    debouncedRun(trimmed);
  };

  watch(query, value => {
    const trimmed = value.trim();
    if (!trimmed) {
      debouncedRun.cancel?.();
      reset();
      return;
    }
    // Consulta nova recomeça da primeira página — sem isso a rolagem seguinte
    // pediria a página da consulta anterior.
    page.value = 1;
    hasMore.value = false;
    isLoading.value = true;
    debouncedRun(trimmed);
  });

  watch([assigneeType, status], rerun);

  return {
    query,
    results,
    isLoading,
    error,
    truncated,
    hasMore,
    loadNextPage,
    retry,
    reset,
  };
}
